import { useEffect, useId, useMemo } from "react";
import { Dialog, type DialogProps, Portal } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Gap, Padding } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { useModal } from "~/providers/ModalProvider";
import { useTheme } from "~/providers/ThemeProvider";

export type CentredModalProps = {
    visible?: boolean;
    readonly name?: string;
} & Omit<DialogProps, "visible" | "theme">;

export const CentredModal = ({ children, name, style, visible = true, ...modalProps }: CentredModalProps) => {
    const { paperTheme } = useTheme();
    const { modalStack, openPartial, closePartial } = useModal();
    const insets = useSafeAreaInsets();
    const modalId = useId();
    const level = useMemo(() => modalStack.indexOf(modalId), [modalStack, modalId]);
    const zIndex = useMemo(() => level * 2000, [level]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(name): name
    const id = useMemo(() => `${name ?? ""}${modalId}`, [modalId]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(id): id
    // biome-ignore lint/correctness/useExhaustiveDependencies(closePartial): action
    useEffect(() => {
        return () => closePartial(id);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies(openPartial): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(closePartial): action
    useEffect(() => {
        visible ? openPartial(id) : closePartial(id);
    }, [visible, id]);

    return (
        <Portal>
            <Dialog
                {...modalProps}
                visible={visible}
                style={[
                    Padding.horizontal_md,
                    Padding.vertical_xl,
                    Gap.xs,
                    {
                        zIndex,
                        paddingTop: insets.top + Space.md,
                        paddingBottom: insets.bottom + Space.md,
                    },
                    style,
                ]}
                theme={paperTheme}
            >
                {children}
            </Dialog>
        </Portal>
    );
};
